import { Box, Typography } from '@mui/material';

type ComputedUsage = {
	value: number;
	registeredEmployees: number;
	range: { min: number; max: number };
};

const UNDER_COLOR = '#e57373'; // מתחת לטווח (תת-שימוש)
const NORMAL_COLOR = '#66bb6a'; // בטווח הנורמה
const HIGH_COLOR = '#42a5f5'; // מעל הטווח (שימוש גבוה)
const TRACK_COLOR = '#e0e0e0';

const W = 280;
const H = 170;
const CX = W / 2;
const CY = 150;
const R = 120;

// 0 (top) is 12 o'clock; -90 = left (low), +90 = right (high)
const polarToCartesian = (angleDeg: number, radius = R) => {
	const a = ((angleDeg - 90) * Math.PI) / 180;
	return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
};

const describeArc = (startAngle: number, endAngle: number) => {
	const start = polarToCartesian(endAngle);
	const end = polarToCartesian(startAngle);
	const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
	return `M ${start.x} ${start.y} A ${R} ${R} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

// value -> angle on the [-90, +90] semicircle, given the domain [0, hi]
const angleForValue = (v: number, hi: number) => {
	const t = Math.max(0, Math.min(1, hi > 0 ? v / hi : 0));
	return -90 + t * 180;
};

const UsageBenchmarkGauge = ({ data }: { data?: ComputedUsage }) => {
	if (!data || data.registeredEmployees <= 0) {
		return null;
	}

	const { value, registeredEmployees, range } = data;
	const { min, max } = range;

	// Keep the normal range readable even when the value is far above it: cap the domain
	// at 1.5x the upper bound and clamp the needle.
	const hi = Math.max(max * 1.5, 1);

	const status =
		value < min
			? { text: 'מתחת לטווח הנורמה (תת-שימוש)', color: UNDER_COLOR }
			: value <= max
			? { text: 'בטווח הנורמה', color: NORMAL_COLOR }
			: { text: 'מעל הטווח (שימוש גבוה)', color: HIGH_COLOR };

	const needleAngle = angleForValue(value, hi);
	const needleTip = polarToCartesian(needleAngle, R - 14);

	const minTick = polarToCartesian(angleForValue(min, hi), R + 6);
	const maxTick = polarToCartesian(angleForValue(max, hi), R + 6);

	return (
		<Box sx={{ display: 'inline-block', textAlign: 'center', direction: 'rtl' }}>
			<Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1d3a8a', mb: 0.5 }}>
				מספר שימושים ביחס לטווח הנורמה
			</Typography>
			<svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
				{/* track */}
				<path d={describeArc(-90, 90)} fill="none" stroke={TRACK_COLOR} strokeWidth={16} strokeLinecap="round" />
				{/* under-range zone */}
				<path
					d={describeArc(-90, angleForValue(min, hi))}
					fill="none"
					stroke={UNDER_COLOR}
					strokeWidth={16}
					strokeLinecap="round"
				/>
				{/* normal zone */}
				<path
					d={describeArc(angleForValue(min, hi), angleForValue(max, hi))}
					fill="none"
					stroke={NORMAL_COLOR}
					strokeWidth={16}
				/>
				{/* high zone */}
				<path
					d={describeArc(angleForValue(max, hi), 90)}
					fill="none"
					stroke={HIGH_COLOR}
					strokeWidth={16}
					strokeLinecap="round"
				/>

				{/* min / max tick labels */}
				<text x={minTick.x} y={minTick.y} fontSize={12} fill="#555" textAnchor="middle">
					{min}
				</text>
				<text x={maxTick.x} y={maxTick.y} fontSize={12} fill="#555" textAnchor="middle">
					{max}
				</text>

				{/* needle */}
				<line x1={CX} y1={CY} x2={needleTip.x} y2={needleTip.y} stroke="#333" strokeWidth={3} strokeLinecap="round" />
				<circle cx={CX} cy={CY} r={6} fill="#333" />

				{/* value */}
				<text x={CX} y={CY - 28} fontSize={30} fontWeight={700} fill="#1d3a8a" textAnchor="middle">
					{value}
				</text>
			</svg>
			<Typography sx={{ fontWeight: 700, color: status.color }}>{status.text}</Typography>
			<Typography variant="caption" color="textSecondary" display="block">
				{`טווח צפוי לפי ${registeredEmployees} משתמשים: ${min}–${max} (×2.55 עד ×3.3)`}
			</Typography>
		</Box>
	);
};

export default UsageBenchmarkGauge;
